<template>
  <div class="carrinho-page">
    <Header />
    
    <div class="container">
      <h1 class="page-title">Carrinho de Compras</h1>
      
      <div v-if="itens.length === 0" class="empty-cart">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H19M17 13V17C17 18.1 17.9 19 19 19C20.1 19 21 18.1 21 17V13M9 19.5C9.8 19.5 10.5 20.2 10.5 21C10.5 21.8 9.8 22.5 9 22.5C8.2 22.5 7.5 21.8 7.5 21C7.5 20.2 8.2 19.5 9 19.5ZM20 19.5C20.8 19.5 21.5 20.2 21.5 21C21.5 21.8 20.8 22.5 20 22.5C19.2 22.5 18.5 21.8 18.5 21C18.5 20.2 19.2 19.5 20 19.5Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p>Seu carrinho está vazio</p>
        <router-link to="/floricultura" class="btn-continue">Continuar Comprando</router-link>
      </div>
      
      <div v-else class="carrinho-content">
        <div class="carrinho-items">
          <div v-for="item in itens" :key="item.id" class="cart-item">
            <div class="item-image">
              <img 
                :src="getImagemUrl(item.imagem, item.nome, item.id)" 
                :alt="item.nome"
                @error="onImageError($event, item.id)"
              />
            </div>
            <div class="item-info">
              <h3 class="item-name">{{ item.nome }}</h3>
              <p class="item-price">R$ {{ formatPrice(item.valor) }}</p>
            </div>
            <div class="item-actions">
              <div class="item-quantity">
                <button @click="diminuirQuantidade(item.id)" class="qty-btn">-</button>
                <span class="qty-value">{{ item.quantidade }}</span>
                <button @click="aumentarQuantidade(item.id)" class="qty-btn">+</button>
              </div>
              <div class="item-total">
                <span class="total-price">R$ {{ formatPrice(item.valor * item.quantidade) }}</span>
              </div>
            </div>
            <button @click="removerItem(item.id)" class="btn-remove">×</button>
          </div>
        </div>
        
        <div class="carrinho-summary">
          <h2 class="summary-title">Resumo do Pedido</h2>
          <div class="summary-row">
            <span>Subtotal:</span>
            <span>R$ {{ formatPrice(total) }}</span>
          </div>
          <div class="summary-row total">
            <span>Total:</span>
            <span>R$ {{ formatPrice(total) }}</span>
          </div>
          <button @click="finalizarCompra" class="btn-checkout">Finalizar Compra</button>
          <router-link to="/floricultura" class="btn-continue-shopping">Continuar Comprando</router-link>
        </div>
      </div>
    </div>
    
    <!-- Dialog de confirmação -->
    <Dialog
      :show="dialog.show"
      :titulo="dialog.titulo"
      :mensagem="dialog.mensagem"
      :tipo="dialog.tipo"
      :botao-texto="dialog.botaoTexto"
      @confirm="onDialogConfirm"
      @cancel="onDialogCancel"
      @close="fecharDialog"
    />
    
    <Footer />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import Footer from '../components/Footer.vue'
import Dialog from '../components/Dialog.vue'
import carrinhoStore from '../store/carrinho'
import vendaService from '../services/vendaService'

export default {
  name: 'Carrinho',
  components: {
    Header,
    Footer,
    Dialog
  },
  data() {
    return {
      itens: [],
      imagensComErro: new Set(), // Para rastrear imagens que falharam
      dialog: {
        show: false,
        titulo: 'Confirmar Remoção',
        mensagem: '',
        tipo: 'confirm',
        botaoTexto: 'Confirmar',
        produtoId: null
      }
    }
  },
  computed: {
    total() {
      return this.itens.reduce((total, item) => total + (item.valor * item.quantidade), 0)
    }
  },
  mounted() {
    this.carregarCarrinho()
  },
  activated() {
    // Recarrega o carrinho ao reentrar na página (ex: ao voltar do assistente)
    this.carregarCarrinho()
  },
  methods: {
    carregarCarrinho() {
      this.itens = carrinhoStore.getCarrinho()
      // Limpar o set de imagens com erro ao recarregar
      this.imagensComErro.clear()
    },
    formatPrice(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    getImagemUrl(imagem, nome, itemId) {
      // Se a imagem já falhou antes, usar placeholder
      if (itemId && this.imagensComErro.has(itemId)) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTYgN0g0QzIuODk1NDMgNyAyIDcuODk1NDMgMiA5VjE5QzIgMjAuMTA0NiAyLjg5NTQzIDIxIDQgMjFIMTZDMTcuMTA0NiAyMSAxOCAyMC4xMDQ2IDE4IDE5VjlDMTggNy44OTU0MyAxNy4xMDQ2IDcgMTYgN1oiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48cGF0aCBkPSJNMTYgMTJMMTIgOEw4IDEyTTEyIDhWMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4='
      }
      
      // Se tem imagem do produto
      if (imagem) {
        // Se já é uma URL completa (http, https ou caminho relativo)
        if (imagem.startsWith('http://') || imagem.startsWith('https://') || imagem.startsWith('/img/') || imagem.startsWith('/')) {
          return imagem
        }

        // Se for nome de arquivo vindo da API (ex: "vaso_teste_20260312191823201.jpg")
        if (typeof imagem === 'string' && imagem.length < 200 && imagem.indexOf('.') !== -1) {
          return `https://api.grupoparque.com.br/produtos/${imagem}`
        }

        // Se já tem data URI completo
        if (imagem.startsWith('data:image')) {
          return imagem
        }

        // Se é base64 sem prefixo (string longa que parece base64)
        // Base64 geralmente tem caracteres alfanuméricos, +, / e = no final
        if (typeof imagem === 'string' && imagem.length > 100 && /^[A-Za-z0-9+/=]+$/.test(imagem)) {
          return `data:image/jpeg;base64,${imagem}`
        }

        // Se não se encaixa em nenhum padrão, tentar como base64 mesmo assim
        return `data:image/jpeg;base64,${imagem}`
      }
      
      // Se não tem imagem, tentar usar imagem padrão baseada no nome do produto
      if (nome) {
        const nomeLower = nome.toLowerCase()
        const mapeamentoImagens = {
          'coroa': '/img/coroa.jpg',
          'coroas': '/img/coroa.jpg',
          'buque': '/img/buque.jpg',
          'buquê': '/img/buque.jpg',
          'buques': '/img/buque.jpg',
          'buquês': '/img/buque.jpg',
          'vaso': '/img/vaso.jpg',
          'vasos': '/img/vaso.jpg',
          'arranjo': '/img/arranjo.jpg',
          'arranjos': '/img/arranjo.jpg',
          'cremacao': '/img/cremacao.jpg',
          'cremação': '/img/cremacao.jpg',
          'cremações': '/img/cremacao.jpg',
          'jazigo': '/img/jazigo.jpg',
          'jazigos': '/img/jazigo.jpg',
          'plano': '/img/plano.jpg',
          'planos': '/img/plano.jpg',
          'urna': '/img/urna.jpg',
          'urnas': '/img/urna.jpg',
          'cinerária': '/img/urna.jpg',
          'cineraria': '/img/urna.jpg',
          'cinerárias': '/img/urna.jpg'
        }
        
        for (const [key, imgPath] of Object.entries(mapeamentoImagens)) {
          if (nomeLower.includes(key)) {
            return imgPath
          }
        }
      }
      
      // Placeholder SVG em vez de imagem padrão que pode não existir
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTYgN0g0QzIuODk1NDMgNyAyIDcuODk1NDMgMiA5VjE5QzIgMjAuMTA0NiAyLjg5NTQzIDIxIDQgMjFIMTZDMTcuMTA0NiAyMSAxOCAyMC4xMDQ2IDE4IDE5VjlDMTggNy44OTU0MyAxNy4xMDQ2IDcgMTYgN1oiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48cGF0aCBkPSJNMTYgMTJMMTIgOEw4IDEyTTEyIDhWMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4='
    },
    aumentarQuantidade(produtoId) {
      const item = this.itens.find(i => i.id === produtoId)
      if (item && item.quantidade < item.qtdEstoque) {
        this.itens = carrinhoStore.atualizarQuantidade(produtoId, item.quantidade + 1)
      }
    },
    diminuirQuantidade(produtoId) {
      const item = this.itens.find(i => i.id === produtoId)
      if (item && item.quantidade > 1) {
        this.itens = carrinhoStore.atualizarQuantidade(produtoId, item.quantidade - 1)
      }
    },
    removerItem(produtoId) {
      this.dialog = {
        show: true,
        titulo: 'Confirmar Remoção',
        mensagem: 'Deseja remover este item do carrinho?',
        tipo: 'confirm',
        botaoTexto: 'Confirmar',
        produtoId: produtoId
      }
    },
    mostrarDialog(titulo, mensagem, tipo = 'confirm', callback = null) {
      this.dialog = {
        show: true,
        titulo,
        mensagem,
        tipo,
        botaoTexto: tipo === 'confirm' ? 'Confirmar' : 'OK',
        callback,
        produtoId: null
      }
    },
    fecharDialog() {
      this.dialog.show = false
    },
    onDialogConfirm() {
      if (this.dialog.produtoId !== null) {
        this.itens = carrinhoStore.removerItem(this.dialog.produtoId)
      }
      if (this.dialog.callback) {
        this.dialog.callback()
      }
      this.fecharDialog()
    },
    onDialogCancel() {
      this.fecharDialog()
    },
    async finalizarCompra() {
      // Sempre buscar dados frescos do localStorage (importante quando o carrinho vem do assistente)
      const itensAtuais = carrinhoStore.getCarrinho()
      if (itensAtuais.length === 0) {
        this.mostrarDialog('Carrinho vazio', 'Adicione itens ao carrinho antes de finalizar a compra.', 'alert')
        return
      }

      try {
        // Extrair IDs dos produtos do carrinho, considerando a quantidade
        const produtoIds = []
        itensAtuais.forEach(item => {
          const id = item.id
          if (id != null && id !== '') {
            const qtd = item.quantidade || 1
            for (let i = 0; i < qtd; i++) {
              produtoIds.push(id)
            }
          }
        })

        if (produtoIds.length === 0) {
          this.mostrarDialog('Erro', 'Nenhum produto válido no carrinho. Verifique os itens e tente novamente.', 'alert')
          return
        }
        
        // Validar se os produtos podem ser vendidos juntos
        const resultado = await vendaService.validarProdutosParaVenda(produtoIds)
        
        if (!resultado.valido) {
          // Construir mensagem detalhada com os conflitos
          let mensagem = ''
          
          if (resultado.conflitos && resultado.conflitos.length > 0) {
            // Verificar se há conflitos de produto duplicado
            const produtosDuplicados = resultado.conflitos.filter(c => c.tipo === 'PRODUTO_DUPLICADO')
            const outrosConflitos = resultado.conflitos.filter(c => c.tipo !== 'PRODUTO_DUPLICADO')
            
            if (produtosDuplicados.length > 0) {
              mensagem += 'Os seguintes produtos não podem ser adicionados mais de uma vez:<br><br>'
              produtosDuplicados.forEach(conflito => {
                mensagem += `<strong>• ${conflito.produtoNome}</strong><br>`
                mensagem += `&nbsp;&nbsp;${conflito.mensagem}<br><br>`
              })
            }
            
            if (outrosConflitos.length > 0) {
              if (produtosDuplicados.length > 0) {
                mensagem += '<br>'
              }
              mensagem += 'Os produtos no seu carrinho não podem ser vendidos juntos:<br><br>'
              outrosConflitos.forEach(conflito => {
                if (conflito.categoriaNome && conflito.produtos) {
                  mensagem += `<strong>Categoria: ${conflito.categoriaNome}</strong><br>`
                  conflito.produtos.forEach(produto => {
                    mensagem += `&nbsp;&nbsp;• ${produto.nome}<br>`
                  })
                  mensagem += '<br>'
                }
              })
            }
            
            mensagem += 'Por favor, ajuste seu carrinho para continuar.'
          } else {
            mensagem = 'Os produtos no seu carrinho não podem ser vendidos juntos. Por favor, remova alguns itens para continuar.'
          }
          
          // Mostrar dialog de erro e não permitir avançar
          this.mostrarDialog(
            'Produtos Incompatíveis',
            mensagem,
            'alert'
          )
          return
        }
        
        // Se válido, prosseguir para checkout
        this.$router.push('/checkout')
      } catch (error) {
        console.error('Erro ao validar produtos:', error)
        this.mostrarDialog(
          'Erro',
          'Não foi possível validar os produtos. Tente novamente.',
          'alert'
        )
      }
    },
    onImageError(event, itemId, nome) {
      // Marcar que esta imagem falhou para evitar loop
      if (itemId && this.imagensComErro.has(itemId)) {
        // Se já tentou antes, usar placeholder direto
        event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTYgN0g0QzIuODk1NDMgNyAyIDcuODk1NDMgMiA5VjE5QzIgMjAuMTA0NiAyLjg5NTQzIDIxIDQgMjFIMTZDMTcuMTA0NiAyMSAxOCAyMC4xMDQ2IDE4IDE5VjlDMTggNy44OTU0MyAxNy4xMDQ2IDcgMTYgN1oiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48cGF0aCBkPSJNMTYgMTJMMTIgOEw4IDEyTTEyIDhWMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4='
        event.target.onerror = null
        return
      }
      
      if (itemId) {
        this.imagensComErro.add(itemId)
      }
      
      // Tentar usar imagem padrão baseada no nome do produto
      if (nome) {
        const nomeLower = nome.toLowerCase()
        const mapeamentoImagens = {
          'coroa': '/img/coroa.jpg',
          'coroas': '/img/coroa.jpg',
          'buque': '/img/buque.jpg',
          'buquê': '/img/buque.jpg',
          'vaso': '/img/vaso.jpg',
          'vasos': '/img/vaso.jpg',
          'arranjo': '/img/arranjo.jpg',
          'arranjos': '/img/arranjo.jpg',
          'cremacao': '/img/cremacao.jpg',
          'cremação': '/img/cremacao.jpg',
          'jazigo': '/img/jazigo.jpg',
          'jazigos': '/img/jazigo.jpg',
          'plano': '/img/plano.jpg',
          'urna': '/img/urna.jpg',
          'urnas': '/img/urna.jpg',
          'cinerária': '/img/urna.jpg',
          'cineraria': '/img/urna.jpg',
          'cinerárias': '/img/urna.jpg'
        }
        
        for (const [key, imgPath] of Object.entries(mapeamentoImagens)) {
          if (nomeLower.includes(key)) {
            // Tentar carregar a imagem padrão
            event.target.src = imgPath
            return
          }
        }
      }
      
      // Se não encontrou imagem padrão, usar placeholder SVG
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTYgN0g0QzIuODk1NDMgNyAyIDcuODk1NDMgMiA5VjE5QzIgMjAuMTA0NiAyLjg5NTQzIDIxIDQgMjFIMTZDMTcuMTA0NiAyMSAxOCAyMC4xMDQ2IDE4IDE5VjlDMTggNy44OTU0MyAxNy4xMDQ2IDcgMTYgN1oiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48cGF0aCBkPSJNMTYgMTJMMTIgOEw4IDEyTTEyIDhWMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4='
      event.target.onerror = null // Prevenir loop infinito
    },
    onImageLoad(itemId) {
      // Se a imagem carregou com sucesso, remover do set de erros
      if (itemId) {
        this.imagensComErro.delete(itemId)
      }
    }
  }
}
</script>

<style scoped>
.carrinho-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 32px;
}

.empty-cart {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 12px;
}

.empty-cart svg {
  color: #9ca3af;
  margin-bottom: 24px;
}

.empty-cart p {
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 24px;
}

.btn-continue {
  display: inline-block;
  background: #34894d;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-continue:hover {
  background: #2d7a42;
}

.carrinho-content {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 32px;
}

.carrinho-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: grid;
  grid-template-columns: 80px 1fr auto auto;
  gap: 20px;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.item-image {
  width: 80px;
  height: 80px;
  min-width: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.item-price {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.item-quantity {
  display: flex;
  align-items: center;
  gap: 12px;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.qty-btn:hover {
  border-color: #34894d;
  color: #34894d;
}

.qty-value {
  font-weight: 600;
  min-width: 30px;
  text-align: center;
}

.item-total {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.btn-remove {
  width: 36px;
  height: 36px;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 6px;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove:hover {
  background: #dc2626;
  color: white;
}

.carrinho-summary {
  background: white;
  border-radius: 12px;
  padding: 24px;
  height: fit-content;
  position: sticky;
  top: 100px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.summary-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  color: #4b5563;
}

.summary-row.total {
  border-bottom: none;
  border-top: 2px solid #e5e7eb;
  margin-top: 12px;
  padding-top: 16px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.btn-checkout {
  width: 100%;
  background: #34894d;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 24px;
}

.btn-checkout:hover {
  background: #2d7a42;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(52, 137, 77, 0.3);
}

.btn-continue-shopping {
  display: block;
  text-align: center;
  margin-top: 12px;
  color: #34894d;
  text-decoration: none;
  font-weight: 500;
}

.btn-continue-shopping:hover {
  text-decoration: underline;
}

@media (max-width: 968px) {
  .carrinho-content {
    grid-template-columns: 1fr;
  }

  .cart-item {
    grid-template-columns: 60px 1fr auto;
    grid-template-areas:
      "img info remove"
      "img actions actions";
    gap: 12px;
    align-items: start;
  }

  .item-image {
    grid-area: img;
    width: 60px;
    height: 60px;
    min-width: 60px;
  }

  .item-info {
    grid-area: info;
    min-width: 0;
  }

  .item-actions {
    grid-area: actions;
    flex-wrap: wrap;
    gap: 12px;
  }

  .btn-remove {
    grid-area: remove;
  }

  .carrinho-summary {
    position: static;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 24px 16px;
  }

  .cart-item {
    padding: 16px;
    grid-template-columns: 50px 1fr auto;
    gap: 10px;
  }

  .item-image {
    width: 50px;
    height: 50px;
    min-width: 50px;
  }

  .item-name {
    font-size: 0.875rem;
  }

  .item-price {
    font-size: 0.75rem;
  }

  .item-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>

